import {
  Bot,
  Files,
  GitCompare,
  GitPullRequest,
  Globe,
  LibraryBig,
  ListChecks,
  MessageSquare,
  Monitor,
  MonitorCog,
  Network,
  Plus,
  type Square,
} from 'lucide-react';
import type { KeyboardShortcut } from '@/lib/platform';

/** Every view the side panel can display, one per tool tab. */
export type { PanelView as SidePanelView } from '@crowdex/host-kit/host';

import type { PanelView as SidePanelView } from '@crowdex/host-kit/host';

/** Static metadata (label, icon, shortcut) describing a side-panel view. */
export type SidePanelItem = {
  id: SidePanelView;
  label: string;
  summaryLabel?: string;
  summaryPresentation?: 'list' | 'dedicated' | 'hidden';
  multiple?: boolean;
  icon: typeof Square;
  /** Opens the view from anywhere; formatted with `shortcutLabel` when shown. */
  shortcut?: KeyboardShortcut | undefined;
  newInstanceLabel?: string;
  /** What it is for, shown when it is offered in a new tab. */
  description?: string | undefined;
  /** An app's own icon (`display.icon`), painted like `icon` (`ItemIcon`). */
  iconSrc?: string | undefined;
};

/** The ordered tool tabs shown in the side panel, keyed by {@link SidePanelView}. */
export const SIDE_PANEL_ITEMS = [
  {
    id: 'plan',
    label: 'Plan',
    summaryPresentation: 'dedicated',
    icon: ListChecks,
  },
  {
    id: 'review',
    label: 'Review',
    summaryPresentation: 'dedicated',
    // Not the pull request's: both tabs can be open side by side.
    icon: GitCompare,
    shortcut: { key: 'g', commandOrControl: true, shift: true },
  },
  {
    id: 'pull-request',
    label: 'Pull request',
    summaryPresentation: 'dedicated',
    icon: GitPullRequest,
  },
  {
    id: 'files',
    label: 'Files',
    summaryPresentation: 'list',
    icon: Files,
    shortcut: { key: 'p', commandOrControl: true },
    description: 'Browse and edit the conversation’s Dossier',
  },
  {
    id: 'results',
    label: 'Library',
    summaryPresentation: 'dedicated',
    icon: LibraryBig,
    description: 'What this conversation produced',
  },
  {
    id: 'chat',
    label: 'Side chat',
    summaryPresentation: 'list',
    icon: MessageSquare,
    // Its one shortcut is the conversation's (⇧⌘L, the ⋮'s Side chat).
    description: 'Ask something on the side',
  },
  {
    id: 'subagent',
    label: 'Sub-agent',
    summaryPresentation: 'dedicated',
    icon: Bot,
  },
  {
    id: 'cloud-browser',
    label: 'Cloud browser',
    summaryPresentation: 'list',
    icon: Globe,
    description: "The cloud workspace's browser",
  },
  {
    id: 'ports',
    label: 'Ports',
    summaryPresentation: 'list',
    icon: Network,
    description: 'Servers running in the workspace',
  },
  {
    id: 'desktop',
    label: 'Desktop',
    summaryPresentation: 'list',
    icon: Monitor,
    description: "The cloud workspace's screen",
  },
  {
    id: 'computer',
    label: 'Computer',
    summaryPresentation: 'hidden',
    icon: MonitorCog,
    description: 'The apps of this Mac the agents may use',
  },
  {
    id: 'new-tab',
    label: 'New tab',
    summaryPresentation: 'hidden',
    icon: Plus,
  },
] as const satisfies readonly SidePanelItem[];
